/**
 * CloudWatch RUM (Real User Monitoring) Utility
 * 
 * This module provides utilities for tracking frontend metrics and user interactions
 * using AWS CloudWatch RUM.
 * 
 * Usage:
 *   import { initRum, trackPageView, trackCustomEvent } from './utils/cloudwatchRum';
 *   
 *   // Initialize RUM (call once in main.tsx)
 *   initRum();
 *   
 *   // Track page view
 *   trackPageView('/dashboard');
 *   
 *   // Track custom event
 *   trackCustomEvent('ButtonClick', { buttonName: 'ImportJob' });
 */

interface RumConfig {
  sessionSampleRate?: number;
  identityPoolId?: string; // Optional - required only if using Cognito Identity Pool for signing
  endpoint?: string;
  telemetries?: string[];
  allowCookies?: boolean;
  enableXRay?: boolean;
  signing?: boolean; // If true and no identityPoolId, requires public resource policy
  guestRoleArn?: string; // Optional - will use default role from identity pool if not provided
  disableAutoPageView?: boolean; // Disable automatic page view recording (we track manually)
}

let rumClient: any = null;
let isInitialized = false;
const eventQueue: Array<{ type: 'pageView' | 'event'; data: any }> = [];

/**
 * Initialize CloudWatch RUM
 * Call this once in main.tsx after the app mounts
 * 
 * Matches the AWS-provided snippet format
 */
export function initRum() {
  // Only initialize once
  if (isInitialized) {
    return;
  }

  const applicationId = import.meta.env.VITE_CLOUDWATCH_RUM_APPLICATION_ID;
  const applicationRegion = import.meta.env.VITE_CLOUDWATCH_RUM_REGION || 'us-west-2';
  const identityPoolId = import.meta.env.VITE_CLOUDWATCH_RUM_IDENTITY_POOL_ID; // Optional - only needed if using Cognito Identity Pool
  const guestRoleArn = import.meta.env.VITE_CLOUDWATCH_RUM_GUEST_ROLE_ARN; // Optional
  const endpoint = import.meta.env.VITE_CLOUDWATCH_RUM_ENDPOINT; // Optional, will be auto-generated if not provided

  // Only APPLICATION_ID is required
  if (!applicationId) {
    return;
  }

  try {
    // Dynamically import AWS RUM SDK
    import('aws-rum-web').then((AWSRUM) => {
      try {
        const { AwsRum } = AWSRUM;

        // Build endpoint URL if not provided (format: https://dataplane.rum.{region}.amazonaws.com)
        const rumEndpoint = endpoint || `https://dataplane.rum.${applicationRegion}.amazonaws.com`;

        const config: RumConfig = {
          sessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
          endpoint: rumEndpoint,
          telemetries: ['performance', 'errors', 'http'],
          allowCookies: true,
          enableXRay: false, // Set to false by default (can be enabled if needed)
          signing: true, // Use signing - requires identityPoolId OR public resource policy
          // Disable automatic page view recording since we're manually tracking
          // This prevents duplicate page views
          disableAutoPageView: true,
        };

        // Add identityPoolId only if provided (optional - required only if using Cognito Identity Pool)
        if (identityPoolId) {
          config.identityPoolId = identityPoolId;
        }

        // Add guestRoleArn only if provided (optional - only used if identityPoolId is set)
        if (guestRoleArn) {
          config.guestRoleArn = guestRoleArn;
        }

        const applicationVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';

        rumClient = new AwsRum(
          applicationId,
          applicationVersion,
          applicationRegion,
          config
        );

        // Verify the client was created successfully
        if (rumClient) {
          isInitialized = true;
          
          // Process any queued events
          if (eventQueue.length > 0) {
            eventQueue.forEach((queuedEvent) => {
              try {
                if (queuedEvent.type === 'pageView') {
                  const pageViewData = typeof queuedEvent.data.path === 'string'
                    ? { pageId: queuedEvent.data.path }
                    : queuedEvent.data.path;
                  rumClient.recordPageView(pageViewData);
                } else if (queuedEvent.type === 'event') {
                  rumClient.recordEvent(queuedEvent.data.eventName, queuedEvent.data.eventData || {});
                }
              } catch (error) {
                console.error('Failed to process queued RUM event:', error);
              }
            });
            eventQueue.length = 0; // Clear the queue
          }
        }
      } catch (constructorError) {
        console.error('Failed to create CloudWatch RUM client:', constructorError);
      }
    }).catch((importError) => {
      console.error('Failed to load AWS RUM SDK:', importError);
    });
  } catch (error) {
    console.error('Failed to initialize CloudWatch RUM:', error);
  }
}

/**
 * Track a page view
 * @param path - The page path (e.g., '/dashboard', '/profile')
 */
export function trackPageView(path: string) {
  if (!rumClient || !isInitialized) {
    // Queue the event if RUM is not ready yet
    eventQueue.push({ type: 'pageView', data: { path } });
    return;
  }

  try {
    // AWS RUM recordPageView accepts either a string or an object
    // Using object format for better control and compatibility
    const pageViewData = typeof path === 'string' 
      ? { pageId: path }
      : path;
    
    rumClient.recordPageView(pageViewData);
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
}

/**
 * Track a custom event
 * @param eventName - Name of the event
 * @param eventData - Additional event data (optional)
 */
export function trackCustomEvent(eventName: string, eventData?: Record<string, any>) {
  if (!rumClient || !isInitialized) {
    // Queue the event if RUM is not ready yet
    eventQueue.push({ type: 'event', data: { eventName, eventData } });
    return;
  }

  try {
    rumClient.recordEvent(eventName, eventData || {});
  } catch (error) {
    console.error('Failed to track custom event:', error);
  }
}

/**
 * Track button click
 * @param buttonName - Name/identifier of the button
 * @param additionalData - Additional context data
 */
export function trackButtonClick(buttonName: string, additionalData?: Record<string, any>) {
  trackCustomEvent('ButtonClick', {
    buttonName,
    ...additionalData,
  });
}

/**
 * Track API call duration
 * @param apiName - Name of the API endpoint
 * @param duration - Duration in milliseconds
 * @param success - Whether the call was successful
 */
export function trackApiCall(apiName: string, duration: number, success: boolean) {
  trackCustomEvent('ApiCall', {
    apiName,
    duration,
    success,
  });
}

/**
 * Track page load time
 * @param pageName - Name of the page
 * @param loadTime - Load time in milliseconds
 */
export function trackPageLoad(pageName: string, loadTime: number) {
  trackCustomEvent('PageLoad', {
    pageName,
    loadTime,
  });
}

/**
 * Track workflow step completion
 * @param workflowName - Name of the workflow
 * @param stepName - Name of the step
 */
export function trackWorkflowStep(workflowName: string, stepName: string) {
  trackCustomEvent('WorkflowStep', {
    workflowName,
    stepName,
  });
}

/**
 * Check if RUM is initialized and ready to use
 * @returns true if RUM is initialized and ready
 */
export function isRumReady(): boolean {
  return isInitialized && rumClient !== null;
}

/**
 * Get RUM initialization status for debugging
 * @returns Status object with initialization details
 */
export function getRumStatus() {
  return {
    isInitialized,
    hasClient: rumClient !== null,
    hasApplicationId: !!import.meta.env.VITE_CLOUDWATCH_RUM_APPLICATION_ID,
    applicationId: import.meta.env.VITE_CLOUDWATCH_RUM_APPLICATION_ID,
    applicationRegion: import.meta.env.VITE_CLOUDWATCH_RUM_REGION || 'us-west-2',
    hasIdentityPoolId: !!import.meta.env.VITE_CLOUDWATCH_RUM_IDENTITY_POOL_ID,
    queuedEvents: eventQueue.length,
  };
}

/**
 * Expose RUM utilities to window for debugging in browser console
 * Only in development mode
 */
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).__RUM_DEBUG__ = {
    getStatus: () => {
      const status = getRumStatus();
      console.log('RUM Status:', status);
      return status;
    },
    isReady: () => {
      const ready = isRumReady();
      console.log('RUM Ready:', ready);
      return ready;
    },
    trackPageView: (path: string) => {
      console.log('Debug: Tracking page view:', path);
      trackPageView(path);
      const status = getRumStatus();
      if (!status.isInitialized) {
        console.warn('⚠️ RUM not initialized yet - event will be queued');
      } else {
        console.log('✅ Page view tracked');
      }
    },
    trackEvent: (eventName: string, eventData?: Record<string, any>) => {
      console.log('Debug: Tracking event:', eventName, eventData);
      trackCustomEvent(eventName, eventData);
      const status = getRumStatus();
      if (!status.isInitialized) {
        console.warn('⚠️ RUM not initialized yet - event will be queued');
      } else {
        console.log('✅ Event tracked');
      }
    },
    testEvent: () => {
      const status = getRumStatus();
      const testData = { 
        timestamp: new Date().toISOString(),
        test: true 
      };
      
      console.log('🧪 Testing RUM event tracking...');
      console.log('RUM Status before:', status);
      
      // Check if client exists and has methods
      if (rumClient) {
        console.log('RUM Client details:', {
          hasRecordEvent: typeof rumClient.recordEvent === 'function',
          hasRecordPageView: typeof rumClient.recordPageView === 'function',
          clientKeys: Object.keys(rumClient).slice(0, 10), // First 10 keys
        });
      }
      
      trackCustomEvent('DebugTestEvent', testData);
      
      // Check status after
      const statusAfter = getRumStatus();
      console.log('RUM Status after:', statusAfter);
      
      // Wait a moment and check network
      setTimeout(() => {
        console.log('📡 After 2 seconds - check Network tab for:');
        console.log('   - Filter: "rum" or "dataplane"');
        console.log('   - Look for: dataplane.rum.us-west-2.amazonaws.com');
        console.log('   - If no requests, possible issues:');
        console.log('     1. Domain not in RUM app monitor allowed list');
        console.log('     2. Signing/authentication issue');
        console.log('     3. RUM client not actually sending (check browser console for errors)');
      }, 2000);
      
      if (statusAfter.isInitialized) {
        console.log('✅ Test event sent! Check Network tab for requests to dataplane.rum.*.amazonaws.com');
        return { success: true, message: 'Event tracked - check Network tab for RUM requests' };
      } else {
        console.warn('⚠️ RUM not initialized - event queued. Queued events:', statusAfter.queuedEvents);
        return { success: false, message: 'RUM not ready - event queued', queuedEvents: statusAfter.queuedEvents };
      }
    },
    inspectClient: () => {
      if (!rumClient) {
        console.log('❌ RUM client is null');
        return { error: 'RUM client not initialized' };
      }
      
      const inspection = {
        hasRecordPageView: typeof rumClient.recordPageView === 'function',
        hasRecordEvent: typeof rumClient.recordEvent === 'function',
        methods: Object.getOwnPropertyNames(Object.getPrototypeOf(rumClient)),
        clientKeys: Object.keys(rumClient),
        clientType: rumClient.constructor?.name,
      };
      
      console.log('RUM Client Inspection:', inspection);
      return inspection;
    },
    verifyDomain: () => {
      const currentDomain = window.location.hostname;
      const currentOrigin = window.location.origin;
      
      console.log('🌐 Domain Verification:');
      console.log('   Current domain:', currentDomain);
      console.log('   Current origin:', currentOrigin);
      console.log('');
      console.log('⚠️ IMPORTANT: Your domain must be in the RUM app monitor allowed list!');
      console.log('   To fix:');
      console.log('   1. Go to AWS CloudWatch Console → RUM → Applications');
      console.log('   2. Select your app monitor (ID: ' + import.meta.env.VITE_CLOUDWATCH_RUM_APPLICATION_ID + ')');
      console.log('   3. Click "Edit" → "Application domains"');
      console.log('   4. Add: ' + currentDomain);
      console.log('   5. For localhost, add: "localhost" or "localhost:3000"');
      console.log('');
      console.log('   If domain is not allowed, RUM will initialize but NOT send data!');
      
      return {
        domain: currentDomain,
        origin: currentOrigin,
        applicationId: import.meta.env.VITE_CLOUDWATCH_RUM_APPLICATION_ID,
      };
    },
    checkNetwork: () => {
      console.log('📡 To check if RUM is sending data:');
      console.log('1. Open DevTools → Network tab');
      console.log('2. Filter by "rum" or "dataplane"');
      console.log('3. Trigger an event or navigate to a page');
      console.log('4. Look for requests to: dataplane.rum.us-west-2.amazonaws.com');
      console.log('5. Check for 403 (permissions) or CORS errors');
    },
  };
  console.log('🔧 CloudWatch RUM Debug Tools available at window.__RUM_DEBUG__');
  console.log('   Try: window.__RUM_DEBUG__.testEvent() or window.__RUM_DEBUG__.getStatus()');
}

export default {
  initRum,
  trackPageView,
  trackCustomEvent,
  trackButtonClick,
  trackApiCall,
  trackPageLoad,
  trackWorkflowStep,
  isRumReady,
  getRumStatus,
};

